import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Mail, KeyRound, CheckCircle2, Phone } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { NovatusLogo } from "../components/NovatusLogo";

// Country dial codes for phone login. DE first (default for the academy market).
const DIAL_CODES = [
  { code: "+49", label: "DE", flag: "🇩🇪" },
  { code: "+43", label: "AT", flag: "🇦🇹" },
  { code: "+41", label: "CH", flag: "🇨🇭" },
  { code: "+39", label: "IT", flag: "🇮🇹" },
  { code: "+33", label: "FR", flag: "🇫🇷" },
  { code: "+44", label: "UK", flag: "🇬🇧" },
  { code: "+31", label: "NL", flag: "🇳🇱" },
  { code: "+34", label: "ES", flag: "🇪🇸" },
  { code: "+1",  label: "US", flag: "🇺🇸" },
];

const Login = () => {
  const [step, setStep] = useState("entry");
  const [mode, setMode] = useState("email"); // "email" | "phone"
  const [email, setEmail] = useState("");
  const [dialCode, setDialCode] = useState("+49");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [isDemo, setIsDemo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registerData, setRegisterData] = useState({ first_name: "", last_name: "", company: "" });
  const { login } = useAuth();
  const navigate = useNavigate();

  // The "identifier" sent to the backend = email OR normalized E.164 phone
  const buildPhone = () => `${dialCode}${phoneLocal.replace(/\D/g, "").replace(/^0+/, "")}`;
  const currentIdentifier = () =>
    mode === "email" ? { email } : { phone: buildPhone() };
  const identifierLabel = () =>
    mode === "email" ? email : buildPhone();

  const requestCode = async (e) => {
    e?.preventDefault?.();
    if (mode === "email" && !email) return toast.error("Bitte E-Mail eingeben");
    if (mode === "phone" && phoneLocal.replace(/\D/g, "").length < 6)
      return toast.error("Bitte gültige Telefonnummer eingeben");

    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", currentIdentifier());
      setDevCode(data.dev_code || "");
      setIsDemo(!!data.is_demo);
      setStep("otp");
      if (data.is_demo) {
        toast.success("Demo-Login", {
          description: `Dein Code steht direkt im Bildschirm — keine E-Mail`,
        });
      } else if (data.delivered && mode === "email") {
        toast.success("Code per E-Mail versendet", {
          description: `Prüfe dein Postfach: ${email}`,
        });
      } else if (data.channel === "phone") {
        toast.success("Code generiert", {
          description: "SMS-Versand folgt — aktuell Dev-Code im Banner sichtbar",
        });
      } else {
        toast.success("Code generiert", {
          description: data.dev_code ? `Dev-Modus: ${data.dev_code}` : "Bitte E-Mail prüfen",
        });
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setStep("register");
        toast.info(mode === "email" ? "E-Mail noch nicht registriert" : "Telefonnummer noch nicht registriert");
      } else {
        toast.error(err?.response?.data?.detail || "Fehler");
      }
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (code.length < 6) return toast.error("Bitte 6-stelligen Code eingeben");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { ...currentIdentifier(), code });
      login(data.token, data.user);
      toast.success(`Willkommen, ${data.user.first_name}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code ungültig");
    } finally { setLoading(false); }
  };

  const register = async (e) => {
    e?.preventDefault?.();
    if (!registerData.first_name || !registerData.last_name)
      return toast.error("Bitte Vor- und Nachname ausfüllen");
    if (!email) return toast.error("Bitte E-Mail eingeben (Pflicht für Rechnungen + Login)");
    // Phone is optional, but if provided must be valid.
    const phoneTrimmed = phoneLocal.replace(/\D/g, "");
    if (phoneTrimmed.length > 0 && phoneTrimmed.length < 6)
      return toast.error("Telefonnummer ist zu kurz");

    setLoading(true);
    try {
      const payload = {
        first_name: registerData.first_name,
        last_name: registerData.last_name,
        email,
        company: registerData.company || "",
        phone: phoneTrimmed.length >= 6 ? buildPhone() : null,
      };
      await api.post("/auth/register", payload);
      // After signup, request the OTP via email (the canonical channel).
      const { data } = await api.post("/auth/request-otp", { email });
      setDevCode(data.dev_code || "");
      setIsDemo(!!data.is_demo);
      setStep("otp");
      toast.success("Konto angelegt", {
        description: "Code wurde an deine E-Mail versendet",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };

  const TabSwitch = () => (
    <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-novatus-card-2 border border-novatus" data-testid="login-tab-switch">
      {[
        { id: "email", label: "E-Mail", icon: Mail },
        { id: "phone", label: "Handynummer", icon: Phone },
      ].map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => setMode(id)}
          className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${
            mode === id
              ? "bg-novatus-green text-black shadow-sm"
              : "text-novatus-text-2 hover:text-white"
          }`}
          data-testid={`login-tab-${id}`}
        >
          <Icon className="w-4 h-4" /> {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-novatus-bg bg-dotted flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex items-start gap-4 mb-10">
          <NovatusLogo size={68} />
          <div className="flex-1 leading-tight">
            <h1 className="font-display font-black text-3xl tracking-tight">
              <span className="text-white">NOVATUS </span>
              <span className="text-novatus-green">Energie</span>
            </h1>
            <div className="mt-1 text-[11px] tracking-[0.3em] uppercase text-novatus-text-2 font-semibold">
              SALES ACADEMY · KI-TRAINING
            </div>
          </div>
        </div>

        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white text-center leading-tight">
          {step === "otp"
            ? "Code eingeben."
            : step === "register"
              ? "Lass uns starten."
              : (<>Ihre Vertriebszukunft. <span className="text-novatus-green">Präzise trainiert.</span></>)}
        </h2>

        <div className="nv-card mt-8 p-7 sm:p-8" data-testid="login-card">
          {step === "entry" && (
            <form onSubmit={requestCode} className="space-y-5">
              <div>
                <h3 className="font-display font-bold text-2xl text-white">Anmelden</h3>
                <p className="text-sm text-novatus-text-2 mt-2 leading-relaxed">
                  {mode === "email"
                    ? "E-Mail eingeben — wir senden Ihnen einen 6-stelligen Login-Code."
                    : "Telefonnummer eingeben — der Code kommt per SMS (Dev-Modus: hier sichtbar)."}
                </p>
              </div>

              <TabSwitch />

              {mode === "email" ? (
                <div>
                  <label className="label-eyebrow block mb-2">E-Mail</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-novatus-text-3 pointer-events-none z-10" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vorname@firma.de"
                      className="nv-input w-full h-12 !pl-11"
                      data-testid="login-email-input"
                      autoFocus
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="label-eyebrow block mb-2">Handynummer</label>
                  <div className="flex gap-2">
                    <select
                      value={dialCode}
                      onChange={(e) => setDialCode(e.target.value)}
                      className="nv-input h-12 px-3 w-[7.5rem] font-mono text-base"
                      data-testid="login-dial-code-select"
                    >
                      {DIAL_CODES.map((d) => (
                        <option key={d.code} value={d.code}>{d.flag} {d.code}</option>
                      ))}
                    </select>
                    <div className="relative flex-1">
                      <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-novatus-text-3 pointer-events-none z-10" />
                      <input
                        type="tel"
                        inputMode="tel"
                        value={phoneLocal}
                        onChange={(e) => setPhoneLocal(e.target.value)}
                        placeholder="170 1234567"
                        className="nv-input w-full h-12 !pl-11"
                        data-testid="login-phone-input"
                        autoFocus
                      />
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-green w-full h-12 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="login-request-otp-btn"
              >
                {loading ? "Sende..." : (<>Weiter <ArrowRight className="w-4 h-4" /></>)}
              </button>

              <p className="text-center text-sm text-novatus-text-2 font-medium">
                Noch kein Konto? Sie werden automatisch durch die Registrierung geführt.
              </p>

              <div className="border border-novatus-green/30 rounded-xl px-4 py-3 mt-3 bg-novatus-green/5">
                <p className="text-[11px] tracking-[0.25em] uppercase font-bold text-novatus-green">Test-Zugang</p>
                <p className="text-[11px] text-novatus-text-2 mt-1.5 leading-relaxed">
                  Code erscheint direkt im Bildschirm — keine E-Mail. Ideal für Workshops und Live-Demos.
                </p>
                <div className="mt-2 grid grid-cols-1 gap-1.5">
                  {[
                    ["lena.berger@novatus.demo", "Verkäufer-Demo"],
                    ["peter.klein@novatus.demo", "Direktor-Demo"],
                  ].map(([e, label]) => (
                    <button
                      type="button"
                      key={e}
                      onClick={() => { setMode("email"); setEmail(e); }}
                      className="text-left text-sm text-novatus-text-1 hover:text-novatus-green transition-colors font-mono font-medium"
                      data-testid={`demo-${label.split('-')[0].toLowerCase()}-btn`}
                    >
                      <span className="text-novatus-green">→</span> {e} <span className="text-novatus-text-2">({label})</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="text-center pt-1">
                <p className="text-xs text-novatus-text-2">
                  Noch kein Konto?{" "}
                  <button
                    type="button"
                    onClick={() => setStep("register")}
                    className="text-novatus-green font-bold hover:underline"
                    data-testid="goto-signup-btn"
                  >
                    Konto anlegen
                  </button>
                </p>
              </div>
            </form>
          )}

          {step === "register" && (
            <form onSubmit={register} className="space-y-4">
              <h3 className="font-display font-bold text-2xl text-white">Konto anlegen</h3>
              <p className="text-xs text-novatus-text-2 leading-relaxed">
                Nur <span className="text-novatus-green font-bold">E-Mail</span> ist Pflicht — sie ist
                dein Login + Rechnungs-Adresse. Firma und Handynummer kannst du später ergänzen.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow block mb-2">Vorname *</label>
                  <input value={registerData.first_name} onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })} className="nv-input w-full h-11" data-testid="register-firstname-input" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Nachname *</label>
                  <input value={registerData.last_name} onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })} className="nv-input w-full h-11" data-testid="register-lastname-input" />
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">E-Mail *</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="nv-input w-full h-11" placeholder="dein.name@example.com" data-testid="register-email-input" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Firma <span className="text-novatus-text-3 normal-case tracking-normal">(optional)</span></label>
                <input value={registerData.company} onChange={(e) => setRegisterData({ ...registerData, company: e.target.value })} className="nv-input w-full h-11" placeholder="z.B. Energie GmbH" data-testid="register-company-input" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Handynummer <span className="text-novatus-text-3 normal-case tracking-normal">(optional)</span></label>
                <div className="flex gap-2">
                  <select value={dialCode} onChange={(e) => setDialCode(e.target.value)} className="nv-input h-11 px-3 w-[6.5rem] font-mono text-sm" data-testid="register-dial-code-select">
                    {DIAL_CODES.map((d) => (
                      <option key={d.code} value={d.code}>{d.flag} {d.code}</option>
                    ))}
                  </select>
                  <input type="tel" inputMode="tel" value={phoneLocal} onChange={(e) => setPhoneLocal(e.target.value)} placeholder="170 1234567" className="nv-input w-full h-11 flex-1" data-testid="register-phone-input" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-green w-full h-12 rounded-xl" data-testid="register-submit-btn">
                {loading ? "..." : "Konto anlegen & Code anfordern"}
              </button>
              <button type="button" onClick={() => setStep("entry")} className="text-xs text-novatus-text-3 hover:text-novatus-green">← zurück</button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={verify} className="space-y-5">
              <div>
                <h3 className="font-display font-bold text-2xl text-white">Code-Eingabe</h3>
                <p className="text-sm text-novatus-text-2 mt-2">
                  An <span className="text-white">{identifierLabel()}</span> wurde ein 6-stelliger Code generiert.
                </p>
              </div>

              {devCode && (
                <div className="rounded-xl border border-novatus-green/40 bg-novatus-green/10 p-4 flex items-start gap-3" data-testid="dev-code-banner">
                  <CheckCircle2 className="w-5 h-5 text-novatus-green mt-0.5" />
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-novatus-green">
                      {isDemo ? "Demo-Account" : "Dev-Modus"}
                    </p>
                    <p className="text-sm text-white mt-1">Code: <span className="font-mono font-black text-xl tracking-[0.4em] text-novatus-green">{devCode}</span></p>
                    {isDemo && (
                      <p className="text-[11px] text-novatus-text-2 mt-1.5 leading-relaxed">
                        Dieser Test-Zugang versendet keine E-Mail — gib den Code oben ein um dich einzuloggen.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="label-eyebrow block mb-2">6-stelliger Code</label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-novatus-text-3 pointer-events-none z-10" />
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="••••••"
                    className="nv-input w-full h-14 !pl-11 text-center text-xl tracking-[0.5em] font-mono"
                    data-testid="otp-code-input"
                    autoFocus
                  />
                </div>
              </div>
              <button type="submit" disabled={loading || code.length < 6} className="btn-green w-full h-12 rounded-xl disabled:opacity-50" data-testid="otp-verify-btn">
                {loading ? "Prüfe..." : "Einloggen"}
              </button>
              <button type="button" onClick={() => { setStep("entry"); setCode(""); setDevCode(""); }} className="text-xs text-novatus-text-3 hover:text-novatus-green" data-testid="otp-back-btn">
                ← {mode === "email" ? "andere E-Mail" : "andere Nummer"} verwenden
              </button>
            </form>
          )}
        </div>

        <p className="text-sm text-novatus-text-2 text-center mt-6 leading-relaxed font-medium">
          Mit dem Login akzeptieren Sie die Trainingsregeln der Novatus Academy.<br />
          Keine Passwörter. Keine Cookies. Nur Performance.
        </p>

        <div className="text-center mt-6 text-xs text-novatus-text-3 flex items-center justify-center gap-3 flex-wrap" data-testid="login-legal-links">
          <a href="/impressum" className="hover:text-novatus-green transition-colors" data-testid="login-link-impressum">Impressum</a>
          <span className="opacity-40">·</span>
          <a href="/datenschutz" className="hover:text-novatus-green transition-colors" data-testid="login-link-datenschutz">Datenschutz</a>
          <span className="opacity-40">·</span>
          <a href="/agb" className="hover:text-novatus-green transition-colors" data-testid="login-link-agb">AGB</a>
          <span className="opacity-40">·</span>
          <a href="/widerruf" className="hover:text-novatus-green transition-colors" data-testid="login-link-widerruf">Widerruf</a>
        </div>
      </div>
    </div>
  );
};

export default Login;
